import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Formulario } from '@/types/formulario';
import { PublicFormSettings } from './PublicFormSettings';
import { useUserClients } from '@/hooks/useUserClients';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { X } from 'lucide-react';

interface EditFormularioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formulario: Formulario | null;
  onSubmit: (id: string, data: Partial<Formulario> & { client_ids?: string[] }) => void;
}

export const EditFormularioDialog = ({ open, onOpenChange, formulario, onSubmit }: EditFormularioDialogProps) => {
  const { clients, isLoading: loadingClients } = useUserClients();
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [status, setStatus] = useState<'ativo' | 'inativo' | 'arquivado'>('ativo');
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [pesquisaSatisfacao, setPesquisaSatisfacao] = useState(false);
  const [envioAutomatico, setEnvioAutomatico] = useState(false);

  useEffect(() => {
    if (formulario && open) {
      setTitulo(formulario.titulo);
      setDescricao(formulario.descricao || '');
      setCategoria(formulario.categoria || '');
      setStatus(formulario.status);
      setPesquisaSatisfacao(formulario.pesquisa_satisfacao || false);
      setEnvioAutomatico(formulario.envio_automatico_fechamento || false);

      // Load associated clients from formulario_clientes
      supabase
        .from('formulario_clientes')
        .select('client_id')
        .eq('formulario_id', formulario.id)
        .then(({ data }) => {
          if (data && data.length > 0) {
            setClientIds(data.map((d: any) => d.client_id));
          } else if (formulario.client_id) {
            setClientIds([formulario.client_id]);
          } else {
            setClientIds([]);
          }
        });
    }
  }, [formulario, open]);

  const toggleClient = (clientId: string) => {
    setClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formulario) return;
    if (clientIds.length === 0) {
      alert('Por favor, selecione pelo menos um cliente responsável');
      return;
    }
    
    onSubmit(formulario.id, {
      titulo,
      descricao,
      categoria,
      status,
      client_id: clientIds[0], // Primary client for backward compatibility
      client_ids: clientIds,
      pesquisa_satisfacao: pesquisaSatisfacao,
      envio_automatico_fechamento: pesquisaSatisfacao && envioAutomatico,
    });
  };

  if (!formulario) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Formulário</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="basico" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basico">Configurações Básicas</TabsTrigger>
            <TabsTrigger value="satisfacao">Pesquisa de Satisfação</TabsTrigger>
            <TabsTrigger value="publico">Acesso Público</TabsTrigger>
          </TabsList>
          
          <TabsContent value="basico">
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div>
                <Label htmlFor="titulo">Título do Formulário *</Label>
                <Input
                  id="titulo"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="categoria">Categoria</Label>
                <Input
                  id="categoria"
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                />
              </div>

              <div>
                <Label>Clientes Responsáveis *</Label>
                {clientIds.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1 mb-2">
                    {clientIds.map(id => {
                      const client = clients.find(c => c.id === id);
                      return (
                        <Badge key={id} variant="secondary" className="gap-1">
                          {client?.name || id}
                          <X className="h-3 w-3 cursor-pointer" onClick={() => toggleClient(id)} />
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-1">
                  {loadingClients ? (
                    <p className="text-sm text-muted-foreground p-2">Carregando clientes...</p>
                  ) : clients.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-2">Nenhum cliente disponível</p>
                  ) : (
                    clients.map((client) => (
                      <label
                        key={client.id}
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                      >
                        <Checkbox
                          checked={clientIds.includes(client.id)}
                          onCheckedChange={() => toggleClient(client.id)}
                        />
                        <span className="text-sm">{client.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={(value: any) => setStatus(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="arquivado">Arquivado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </TabsContent>
          
          <TabsContent value="satisfacao">
            <div className="py-4 space-y-6">
              <div className="rounded-lg border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Pesquisa de Satisfação</Label>
                    <p className="text-sm text-muted-foreground">
                      Marcar este formulário como pesquisa de satisfação pós-atendimento
                    </p>
                  </div>
                  <Switch
                    checked={pesquisaSatisfacao}
                    onCheckedChange={setPesquisaSatisfacao}
                  />
                </div>

                {pesquisaSatisfacao && (
                  <div className="flex items-center justify-between pl-4 border-l-2 border-primary/20">
                    <div className="space-y-0.5">
                      <Label>Envio automático ao fechar ticket</Label>
                      <p className="text-sm text-muted-foreground">
                        Enviar automaticamente para o solicitante quando o ticket for fechado
                      </p>
                    </div>
                    <Switch
                      checked={envioAutomatico}
                      onCheckedChange={setEnvioAutomatico}
                    />
                  </div>
                )}
              </div>

              {pesquisaSatisfacao && (
                <div className="rounded-lg bg-muted/50 p-4">
                  <h4 className="font-medium mb-2">Como funciona</h4>
                  <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside">
                    <li>Quando um ticket for fechado, o formulário será enviado automaticamente ao solicitante</li>
                    <li>O formulário exibirá o número e título do ticket para contexto</li>
                    <li>Cada ticket só receberá uma pesquisa (sem duplicatas)</li>
                    <li>As respostas ficam vinculadas ao ticket original para análise</li>
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button onClick={() => handleSubmit()}>
                  Salvar Alterações
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="publico">
            <div className="py-4">
              <PublicFormSettings formulario={formulario} />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
